import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { redirect } from "@tanstack/react-router";

/**
 * Fetches user roles from the API and updates the auth store.
 * This is the single source of truth for role resolution — all auth guards
 * call this instead of duplicating the getAuthMe / setAdminStatus logic.
 */
async function fetchAndSetRoles(): Promise<{
  isAdmin: boolean;
  isManager: boolean;
}> {
  const { getAuthMe } = await import("../features/auth/api/auth.api");
  const userDetails = await getAuthMe();

  const isAdmin =
    userDetails.role_name?.toLowerCase().includes("admin") ?? false;
  const isManager =
    userDetails.role_name?.toLowerCase().includes("manager") ?? false;

  useAuthStore.getState().setAdminStatus(isAdmin);
  useAuthStore.getState().setManagerStatus(isManager);

  return { isAdmin, isManager };
}

/**
 * Resolves role state, fetching from the API only when necessary.
 * Returns the current role flags after ensuring they are available.
 */
async function resolveRoles(): Promise<{
  isAdmin: boolean;
  isManager: boolean;
}> {
  const state = useAuthStore.getState();

  // If roles are already fetched, return cached values
  if (state.hasFetchedRole && state.hasFetchedManagerRole) {
    return { isAdmin: state.isAdmin, isManager: state.isManager };
  }

  // Fetch from API once and populate both admin + manager flags
  return fetchAndSetRoles();
}

// ----------------------------------------------------------------
// Auth Guards
// ----------------------------------------------------------------

/**
 * Requires the user to be authenticated.
 * Redirects admins to /admin/dashboard automatically.
 */
export async function requireAuth() {
  const { token } = useAuthStore.getState();
  if (!token) throw redirect({ to: "/login" });

  try {
    const { isAdmin } = await resolveRoles();
    if (isAdmin) throw redirect({ to: "/admin/dashboard" });
  } catch (error) {
    // Re-throw redirects; swallow API errors so guard doesn't block the page
    if (error instanceof Response || (error as any)?.__type === "redirect")
      throw error;
    console.error("Failed to verify user role:", error);
  }
}

/**
 * Requires the user to be an admin.
 * Non-admins and unauthenticated users are sent to /login.
 */
export async function requireAdmin() {
  const { token } = useAuthStore.getState();
  if (!token) throw redirect({ to: "/login" });

  try {
    const { isAdmin } = await resolveRoles();
    if (!isAdmin) throw redirect({ to: "/login" });
  } catch (error) {
    if (error instanceof Response || (error as any)?.__type === "redirect")
      throw error;
    console.error("Failed to verify admin role:", error);
    useAuthStore.getState().setAdminStatus(false);
    throw redirect({ to: "/login" });
  }
}

/**
 * Requires the user to be a manager.
 * Admins are redirected to /admin/dashboard; hash-key users to landing.
 */
export async function requireManager() {
  const { token, isHashKeyUser } = useAuthStore.getState();
  if (!token) throw redirect({ to: "/login" });
  if (isHashKeyUser())
    throw redirect({ to: "/", search: { section: undefined } });

  try {
    const { isAdmin, isManager } = await resolveRoles();
    if (isAdmin) throw redirect({ to: "/admin/dashboard" });
    if (!isManager) throw redirect({ to: "/login" });
  } catch (error) {
    if (error instanceof Response || (error as any)?.__type === "redirect")
      throw error;
    console.error("Failed to verify manager role:", error);
    useAuthStore.getState().setManagerStatus(false);
    throw redirect({ to: "/login" });
  }
}

/**
 * For public routes: if the user is already logged in as admin, redirect to
 * admin dashboard instead of showing the public page.
 */
export async function checkAdminRedirect() {
  const { token } = useAuthStore.getState();
  if (!token) return;

  try {
    const { isAdmin } = await resolveRoles();
    if (isAdmin) throw redirect({ to: "/admin/dashboard" });
  } catch (error) {
    if (error instanceof Response || (error as any)?.__type === "redirect")
      throw error;
    console.error("Failed to verify user role:", error);
  }
}
